import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { AuthenticationService, AUTH_KEYS } from '../api';
import { trackEvent } from '../analytics/track';

export interface AuthTenant {
  id: number;
  name?: string;
  slug?: string;
  is_active?: string;
}

export interface AuthUser {
  id: number;
  name: string;
  email: string;
  role?: string;
  companion_id?: number;
  roles?: { id: number; slug?: string; description?: string }[];
  permissions?: { id: number; slug?: string }[];
  tenant?: AuthTenant | null;
  people?: { first_name?: string; last_name?: string; mobile?: string; email?: string };
  is_account_setup?: number;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(AUTH_KEYS.user);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Fetch current user on mount if token exists
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(AUTH_KEYS.accessToken);
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await AuthenticationService.e339E0E74860C0647Fd1D41Bbbbc13B2();
        const userData = response.data;
        setUser(userData as AuthUser);
        localStorage.setItem(AUTH_KEYS.user, JSON.stringify(userData));
      } catch {
        // Token invalid - clear auth
        localStorage.removeItem(AUTH_KEYS.accessToken);
        localStorage.removeItem(AUTH_KEYS.refreshToken);
        localStorage.removeItem(AUTH_KEYS.user);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await AuthenticationService.cd5203E2D0A820E113F2F4Fdb2847({ email, password });
      const data = response.data as { token?: string; refresh_token?: string; user?: AuthUser };

      if (data?.token) {
        localStorage.setItem(AUTH_KEYS.accessToken, data.token);
      }
      if (data?.refresh_token) {
        localStorage.setItem(AUTH_KEYS.refreshToken, data.refresh_token);
      }
      if (data?.user) {
        localStorage.setItem(AUTH_KEYS.user, JSON.stringify(data.user));
        setUser(data.user);
      }

      // Login is already captured server-side by ClickLaw API (/auth/login).
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await AuthenticationService.a05022A34Eef599E04Fb0A5254375B84();
    } catch {
      // Ignore logout API errors - clear local state anyway
    } finally {
      // Fire before tokens are cleared so it can use /analytics/track.
      void trackEvent('logout', {});
      localStorage.removeItem(AUTH_KEYS.accessToken);
      localStorage.removeItem(AUTH_KEYS.refreshToken);
      localStorage.removeItem(AUTH_KEYS.user);
      setUser(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await AuthenticationService.e339E0E74860C0647Fd1D41Bbbbc13B2();
      const userData = response.data;
      setUser(userData as AuthUser);
      localStorage.setItem(AUTH_KEYS.user, JSON.stringify(userData));
    } catch {
      // Silent fail - user state remains unchanged
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
